import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fireErrorAnalytics, fireSiteOptedOutAnalytics } from '../analytics/index.tsx';
import { withEligibilityCache } from '../caching/index.tsx';
import { hasProductAccess } from '../permission-check/index.tsx';
import { fetchIsRequestAccessCapable } from '../request-capabilities/index.tsx';
import { isSite7DaysOld } from '../site-eligibility/index.tsx';
import { isSiteOptedOut } from '../site-opt-out/index.tsx';
import { isNewActiveUser } from '../user-eligibility/index.tsx';
import { useOverrides } from '../overrides/index.tsx';
import type { EligibilityCheck } from './types.tsx';

/*
  Eligibility can be overriden using the following query parameter:

  ?cjn_conf_overrides={
    "is_your_work_route": false,
    "site_has_no_licensed_confluence": false,
    "site_is_opted_out": false,
    "user_already_has_confluence": false,
    "user_cannot_request_confluence_access": false,
    "confluence_is_too_new": false,
    "user_is_too_new": false,
  }
*/
type OverrideKey =
	| 'is_your_work_route'
	| 'site_has_no_licensed_confluence'
	| 'site_is_opted_out'
	| 'user_already_has_confluence'
	| 'user_cannot_request_confluence_access'
	| 'confluence_is_too_new'
	| 'user_is_too_new';

export const useMsrConfluenceCrossJoinNudgeEligibility = () => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const userId = useAccountId() || '';
	const licensedProducts = useLicensedProducts();
	const isYourWorkRoute = useIsActiveRoute('your-work');
	const withOverrides = useOverrides<OverrideKey>('cjn_conf_overrides');

	return useCallback(async (): Promise<EligibilityCheck> => {
		try {
			// TODO: PUMA-423 replace with smart caching to further optimize calls
			const cachedIsSiteOptedOut = withEligibilityCache('site_is_opted_out', isSiteOptedOut, 0.2);
			const cachedHasProductAccess = withEligibilityCache(
				'user_already_has_confluence',
				hasProductAccess,
				30,
			);
			const cachedUserCanRequestAccess = withEligibilityCache(
				'user_cannot_request_confluence_access',
				fetchIsRequestAccessCapable,
				7,
			);

			// TODO: PUMA-423 replace with smart caching to further optimize calls
			const cachedConfluenceTooNew = withEligibilityCache(
				'confluence_is_too_new',
				async () => !(await isSite7DaysOld('confluence', cloudId)),
				7,
			);

			// TODO: PUMA-423 replace with smart caching to further optimize calls
			const cachedUserTooNew = withEligibilityCache('user_is_too_new', isNewActiveUser, 7);

			if (withOverrides('is_your_work_route', isYourWorkRoute)) {
				return { isEligible: false, reason: 'is_your_work_route' };
			}

			if (withOverrides('site_has_no_licensed_confluence', !licensedProducts.confluence)) {
				return { isEligible: false, reason: 'site_has_no_licensed_confluence' };
			}

			const userHasProductAccess = await cachedHasProductAccess('confluence', cloudId);
			if (withOverrides('user_already_has_confluence', userHasProductAccess)) {
				return { isEligible: false, reason: 'user_already_has_confluence' };
			}

			const userCanRequestAccessToConfluence = await cachedUserCanRequestAccess(
				'confluence',
				cloudId,
			);
			if (
				withOverrides('user_cannot_request_confluence_access', !userCanRequestAccessToConfluence)
			) {
				return { isEligible: false, reason: 'user_cannot_request_confluence_access' };
			}

			if (withOverrides('site_is_opted_out', await cachedIsSiteOptedOut(cloudId))) {
				fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesConfluence');
				return { isEligible: false, reason: 'site_is_opted_out' };
			}

			if (withOverrides('confluence_is_too_new', await cachedConfluenceTooNew())) {
				return { isEligible: false, reason: 'confluence_is_too_new' };
			}

			if (withOverrides('user_is_too_new', await cachedUserTooNew(cloudId, userId))) {
				return { isEligible: false, reason: 'user_is_too_new' };
			}

			return { isEligible: true, reason: '' };

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('eligibilityCheck', error, { cloudId, targetProduct: 'confluence' });
			return { isEligible: false, reason: 'unexpected error' };
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		isYourWorkRoute,
		licensedProducts.confluence,
		userId,
		withOverrides,
	]);
};
